import { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Clock, Check, MapPin } from "lucide-react";
import { PageSEO } from "@/components/PageSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const HUBSPOT_PORTAL_ID = process.env.REACT_APP_HUBSPOT_PORTAL_ID;
const HUBSPOT_FORM_ID = process.env.REACT_APP_HUBSPOT_FORM_ID;
const HUBSPOT_SUBMIT_URL = `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`;

function PlanFeatureItem({ feature }) {
  return (
    <li className="flex items-start gap-2">
      <Check size={14} className="text-cyan-500 mt-0.5 flex-shrink-0" />
      <span className="text-gray-300 text-xs">{feature}</span>
    </li>
  );
}

function PlanFeaturesSection({ features }) {
  return (
    <ul className="space-y-2">
      {features.map((feature) => (
        <PlanFeatureItem key={feature} feature={feature} />
      ))}
    </ul>
  );
}

function getInitialFormData({ plan, sourcePage } = {}) {
  if (plan) {
    return {
      name: "",
      email: "",
      company: "",
      phone: "",
      service: "Website Building",
      message: "I'm interested in the " + plan.name + " plan (\u00A3" + plan.price + "/month).\n\nPlease provide more information about getting started."
    };
  }
  if (sourcePage) {
    return {
      name: "",
      email: "",
      company: "",
      phone: "",
      service: "",
      message: "Hi, I came from the " + sourcePage + " page and would like to learn more.\n\n"
    };
  }
  return { name: "", email: "", company: "", phone: "", service: "", message: "" };
}

export default function Contact() {
  const location = useLocation();
  const selectedPlan = location.state?.selectedPlan || null;
  const sourcePage = location.state?.sourcePage || null;
  const [formData, setFormData] = useState(() =>
    getInitialFormData({ plan: selectedPlan, sourcePage })
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const nameParts = formData.name.trim().split(/\s+/);
      const firstname = nameParts[0];
      const lastname = nameParts.slice(1).join(" ");

      const fields = [
        { name: "firstname", value: firstname },
        { name: "email", value: formData.email },
        { name: "message", value: formData.message },
      ];
      if (lastname) fields.push({ name: "lastname", value: lastname });
      if (formData.phone) fields.push({ name: "phone", value: formData.phone });
      if (formData.company) fields.push({ name: "company", value: formData.company });
      if (formData.service) fields.push({ name: "service", value: formData.service });

      const payload = {
        fields,
        context: {
          pageUri: typeof window !== "undefined" ? window.location.href : "",
          pageName: "Contact",
        },
      };

      const response = await fetch(HUBSPOT_SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message || "HubSpot submission failed");
      }

      toast.success("Thank you for your message. We'll get back to you soon!");
      setFormData({ name: "", email: "", company: "", phone: "", service: "", message: "" });
      window.history.replaceState({}, document.title);
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-16 lg:pt-20">
      <PageSEO
        title="Contact Us — Get in Touch"
        description="Contact Your-IT-Solutions in Lowestoft, Suffolk. Send us a message for website building, AI automation, Microsoft 365, or IT consulting enquiries. Serving clients across the UK."
        path="/contact"
      />
      <section className="py-20 lg:py-32 hero-gradient relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="text-cyan-400 text-sm font-medium uppercase tracking-widest mb-4 block">Contact Us</span>
            <h1 data-testid="contact-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6">
              Let's Build Something <span className="gradient-text">Great Together</span>
            </h1>
            <p className="text-lg text-gray-400 leading-relaxed">
              Have a project in mind? We'd love to hear about it.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            <motion.div id="contact-form" initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:col-span-3">
              <div className="p-8 lg:p-10 rounded-2xl bg-gray-900/50 border border-gray-800">
                <h2 className="text-2xl font-bold text-white mb-6">Send us a message</h2>
                
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gray-300">Name *</Label>
                      <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="John Smith" className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-300">Email *</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="john@company.com" className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500" required />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-gray-300">Company</Label>
                      <Input id="company" name="company" value={formData.company} onChange={handleChange} placeholder="Your Company" className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-300">Phone</Label>
                      <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="+44 7XXX XXX XXX" className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Service Interested In</Label>
                    <Select value={formData.service} onValueChange={handleServiceChange}>
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        <SelectItem value="Website Building" className="text-white">Website Building</SelectItem>
                        <SelectItem value="AI Agents & Automation" className="text-white">AI Agents & Automation</SelectItem>
                        <SelectItem value="Other / Just a Chat" className="text-white">Other / Just a Chat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-gray-300">Message *</Label>
                    <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your project..." rows={5} className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 resize-none" required />
                  </div>

                  <Button type="submit" disabled={isSubmitting} className="btn-primary text-white font-medium px-8 h-12">
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:col-span-2">
              {selectedPlan ? (
                <div className="p-6 lg:p-8 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 sticky top-24">
                  <h3 className="text-lg font-semibold text-cyan-400 mb-2">Selected Plan</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-white">{selectedPlan.name}</span>
                    <span className="text-gray-400 ml-2">- £{selectedPlan.price}/month</span>
                  </div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">What's Included</p>
                  <PlanFeaturesSection features={selectedPlan.features} />
                </div>
              ) : (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">Get in touch</h2>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                        <Mail size={20} className="text-cyan-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium mb-1">Email</h3>
                        <a href="mailto:contact@your-it-solutions.com" className="text-gray-400 hover:text-cyan-400">contact@your-it-solutions.com</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                        <MapPin size={20} className="text-cyan-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium mb-1">Location</h3>
                        <p className="text-gray-400">Lowestoft, Suffolk, UK</p>
                        <p className="text-gray-500 text-sm">Serving clients nationwide</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                        <Clock size={20} className="text-cyan-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium mb-1">Business Hours</h3>
                        <p className="text-gray-400">Mon - Fri: 8AM - 6PM (UK Time)</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-10 p-6 rounded-2xl bg-gray-900/50 border border-gray-800">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                      <span className="text-green-400 text-sm font-medium">Quick Response</span>
                    </div>
                    <p className="text-gray-400 text-sm">We typically respond within 24 hours.</p>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
